import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import "react-toastify/dist/ReactToastify.css";

function DeleteWarningModal(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    maxHeight: 800,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const isRoleDelete = props.type === "role";
  const isDepartmentDelete = props.type === "department";
  const usesImpactFlow = isRoleDelete || isDepartmentDelete;
  const entityName = isDepartmentDelete
    ? props.selectedItem?.role_type
    : isRoleDelete
      ? props.selectedItem?.role_name
      : props.selectedItem?.name;
  const deleteImpact = props.deleteImpact;
  const deleteImpactLoading = props.deleteImpactLoading;
  const blocksDelete = Boolean(deleteImpact?.blocks_delete);
  const requiresReassignment = Boolean(deleteImpact?.requires_reassignment);
  const replacementValue = isDepartmentDelete
    ? props.replacementDepartmentId
    : props.replacementRoleId;
  const replacementOptions = isDepartmentDelete
    ? props.availableDepartments
    : props.availableRoles;
  const replacementLabel = isDepartmentDelete
    ? "Replacement department"
    : "Replacement role";
  const replacementPlaceholder = isDepartmentDelete
    ? "Select replacement department"
    : "Select replacement role";
  const replacementEmptyLabel = isDepartmentDelete
    ? "No replacement departments are available. Create another department first to reassign these records."
    : "No replacement roles are available. Create another role first to reassign these records.";

  const isDeleteDisabled =
    usesImpactFlow &&
    (
      deleteImpactLoading ||
      blocksDelete ||
      (requiresReassignment && !replacementValue)
    );

  const handleDelete = async () => {
    if (isDeleteDisabled) {
      return;
    }

    if (isRoleDelete) {
      const wasSuccessful = await props.delete_function_role(
        props.selectedItem,
        props.selectedItem?.color,
        "delete",
        props.replacementRoleId ? Number(props.replacementRoleId) : null
      );
      if (wasSuccessful !== false) {
        props.handleClose();
      }
      return;
    }

    if (isDepartmentDelete) {
      const wasSuccessful = await props.delete_function(
        props.selectedItem,
        "delete",
        props.replacementDepartmentId
          ? Number(props.replacementDepartmentId)
          : null
      );
      if (wasSuccessful !== false) {
        props.handleClose();
      }
      return;
    }

    props.handleClose();
    props.delete_function(props.selectedItem, "delete");
  };

  return (
    <Modal
      open={props.open}
      onClose={() => props.handleClose()}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <Box sx={style}>
        <div
          className="absolute right-2 top-2 cursor-pointer text-gray-500"
          onClick={() => props.handleClose()}
        >
          <CloseIcon />
        </div>
        <div>
          <div className="flex items-center space-x-2">
            <div className="text-rose-800">
              <WarningIcon style={{ marginBottom: "1px" }} />
            </div>
            <h3 className="text-2xl text-slate-900">
              {isRoleDelete
                ? "Delete role"
                : isDepartmentDelete
                  ? "Delete department"
                  : "Are you sure?"}
            </h3>
          </div>

          {usesImpactFlow ? (
            <div>
              <p className="mt-4 text-sm text-slate-500">
                Delete <span className="font-bold">{entityName}</span>.
                {isRoleDelete
                  ? " If this role is still referenced, you can move those records to another current role first."
                  : " If this department is still referenced by labor records, you can move those aggregates to another current department first."}
              </p>

              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Impact
                </p>
                {deleteImpactLoading ? (
                  <p className="mt-3 text-sm text-slate-500">
                    Checking current references...
                  </p>
                ) : deleteImpact?.impacts?.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {deleteImpact.impacts.map((impact) => (
                      <div
                        key={impact.key}
                        className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <span>{impact.label}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {impact.count}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-emerald-700">
                    {isRoleDelete
                      ? "No current records need reassignment. This role can be deleted directly."
                      : "No current records need reassignment. This department can be deleted directly."}
                  </p>
                )}
              </div>

              {blocksDelete && (
                <p className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  This department still has roles assigned to it. Move or delete
                  those roles first, then try deleting the department again.
                </p>
              )}

              {requiresReassignment && !blocksDelete && (
                <div className="mt-5">
                  <p className="text-sm font-medium text-slate-700">
                    {replacementLabel}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {isRoleDelete
                      ? "Records currently tied to this role will be reassigned before deletion."
                      : "Labor records currently tied to this department will be reassigned and merged before deletion."}
                  </p>
                  {replacementOptions?.length > 0 ? (
                    <select
                      className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:border-violet-500"
                      value={replacementValue || ""}
                      onChange={(event) => {
                        if (isDepartmentDelete) {
                          props.onReplacementDepartmentChange?.(
                            event.target.value
                          );
                          return;
                        }
                        props.onReplacementRoleChange?.(event.target.value);
                      }}
                    >
                      <option value="" disabled>
                        {replacementPlaceholder}
                      </option>
                      {replacementOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {isDepartmentDelete
                            ? option.role_type
                            : `${option.role_name} (${option.role_type})`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      {replacementEmptyLabel}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="mt-4 text-sm text-slate-500">
                Are you sure you want to delete this {props.type},{" "}
                <span className="font-bold">{entityName}</span>?
              </p>
              <p className="mt-4 text-sm text-slate-500">
                Only perform this action if you are sure that{" "}
                <span className="font-bold">{entityName}</span> is no longer used
                and won't be used in the future.
              </p>
            </div>
          )}

          <div className="mt-8 flex w-full justify-end">
            <div className="flex space-x-3">
              <div
                className="w-fit cursor-pointer rounded-xl border border-violet-700 bg-white px-8 py-2 text-base text-violet-700 hover:bg-slate-100"
                onClick={() => props.handleClose()}
              >
                Cancel
              </div>
              <div
                className={`w-fit rounded-xl px-8 py-2 text-base ${
                  isDeleteDisabled
                    ? "cursor-not-allowed bg-slate-100 text-slate-400"
                    : "cursor-pointer bg-rose-200 text-rose-800 hover:bg-rose-300"
                }`}
                onClick={handleDelete}
              >
                {usesImpactFlow && requiresReassignment && !blocksDelete
                  ? "Reassign and Delete"
                  : isDepartmentDelete && blocksDelete
                    ? "Delete blocked"
                    : "Delete Anyway"}
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default DeleteWarningModal;
